import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { Card, Table, Button, Spin, message, Input } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  CreditCardOutlined,
  LogoutOutlined,
  ReloadOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import api from '../utils/api'
import { formatTz } from '../utils/timezone'

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f5f0 0%, #e8e8e8 50%, #f0ede5 100%);
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const Title = styled.h1`
  font-size: 28px;
  color: #5a5a5a;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    margin-bottom: 10px;
  }
`

const StyledButton = styled(Button)`
  @media (max-width: 768px) {
    width: 100%;
  }
`

const InfoCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  transition: all 0.3s ease;
  background: #faf9f6;
  border: 1px solid rgba(168, 181, 192, 0.2);

  &:hover {
    box-shadow: 0 6px 24px rgba(154, 163, 175, 0.2);
    transform: translateY(-2px);
  }

  .ant-card-head {
    border-bottom: 1.5px solid rgba(168, 181, 192, 0.3);
    background: transparent;
  }

  .ant-card-head-title {
    font-size: 18px;
    font-weight: 500;
    color: #5a5a5a;
    letter-spacing: 0.3px;
  }

  .ant-card-body {
    background: transparent;
  }

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px;
  background: #ffffff;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f7f4;
    border-left-color: #9ca3af;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const InfoLabel = styled.span`
  font-size: 12px;
  color: #8a8a8a;
  margin-bottom: 6px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`

const InfoValue = styled.span`
  font-size: 16px;
  color: #4a4a4a;
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

// 自定义 Tag 组件 - 莫兰迪色系
const CustomTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  border: 1px solid;

  ${(props) => {
    switch (props.variant) {
      case 'success':
        return `
          background: #e8f5e9;
          color: #5a8a5a;
          border-color: #c4d4c4;
        `
      case 'error':
        return `
          background: #fce4ec;
          color: #b85a6a;
          border-color: #e4c4d4;
        `
      case 'warning':
        return `
          background: #fff3e0;
          color: #b8956a;
          border-color: #e4d4c4;
        `
      case 'info':
        return `
          background: #e3f2fd;
          color: #5a7a9a;
          border-color: #c4d4e4;
        `
      case 'blue':
        return `
          background: #e8eef5;
          color: #6a8a9a;
          border-color: #c4d4e4;
        `
      default:
        return `
          background: #f5f5f0;
          color: #8a8a8a;
          border-color: #d4d4c4;
        `
    }
  }}
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 4px 8px;
    gap: 4px;
    border-radius: 6px;
    border: 1px solid;
  }
`

const StatusTag = styled(CustomTag)`
  padding: 6px 14px;
  font-size: 13px;
`

const PaymentRecordsTable = styled(Table)`
  .ant-table {
    border-radius: 8px;
    overflow: hidden;
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`

const SearchContainer = styled.div`
  background: #faf9f6;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  align-items: flex-end;
  border: 1px solid rgba(168, 181, 192, 0.2);

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
  }
`

const FinanceCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  background: linear-gradient(135deg, #faf9f6 0%, #f5f5f0 100%);
  border: 1px solid rgba(168, 181, 192, 0.3);

  .ant-card-head {
    border-bottom: 1.5px solid rgba(168, 181, 192, 0.3);
    background: transparent;
  }

  .ant-card-head-title {
    font-size: 20px;
    font-weight: 500;
    color: #5a5a5a;
    letter-spacing: 0.3px;
  }
`

const FinanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  margin-top: 8px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`

const FinanceItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(168, 181, 192, 0.2);
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(154, 163, 175, 0.2);
  }
`

const FinanceLabel = styled.span`
  font-size: 13px;
  color: #8a8a8a;
  margin-bottom: 8px;
  font-weight: 400;
  letter-spacing: 0.5px;
`

const FinanceValueLarge = styled.span`
  font-size: 24px;
  color: #5a5a5a;
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`
const FinanceValue = styled.span`
  font-size: 24px;
  color: #5a5a5a;
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const SearchInputWrapper = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const SubscriptionInfoPage = () => {
  const [subscriptionData, setSubscriptionData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [merchantOrderNo, setMerchantOrderNo] = useState('')
  const [token, setToken] = useState(null)
  const [calculationMode, setCalculationMode] = useState('monthly') // 'monthly' 或 'yearly'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 检查token并初始化
  useEffect(() => {
    const savedToken = sessionStorage.getItem('subscription_view_token')
    if (!savedToken) {
      message.warning('請先登入')
      navigate('/subscription/login')
      return
    }
    setToken(savedToken)

    // 检查URL参数中是否有merchantOrderNo
    const orderNoFromUrl = searchParams.get('merchantOrderNo')
    if (orderNoFromUrl) {
      setMerchantOrderNo(orderNoFromUrl)
      fetchSubscriptionData(orderNoFromUrl, savedToken)
    }
  }, [navigate, searchParams])

  const fetchSubscriptionData = async (orderNo, tokenToUse = null) => {
    if (!orderNo || !orderNo.trim()) {
      message.warning('請輸入訂單號')
      return
    }

    const currentToken =
      tokenToUse || token || sessionStorage.getItem('subscription_view_token')
    if (!currentToken) {
      message.error('請先登入')
      navigate('/subscription/login')
      return
    }

    try {
      setLoading(true)
      // 根据API文档，需要在请求头中添加Authorization
      const response = await api.get(`/api/subscription/info/${orderNo}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`
        }
      })

      if (response.data.success) {
        setSubscriptionData(response.data.data)
      } else {
        message.error(response.data.message || '獲取訂閱資訊失敗')
      }
    } catch (error) {
      console.error('獲取訂閱資訊錯誤:', error)

      // 处理token过期的情况
      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.message || ''
        if (errorMessage.includes('過期') || errorMessage.includes('無效')) {
          sessionStorage.removeItem('subscription_view_token')
          setToken(null)
          message.error('登入已過期，請重新登入')
          navigate('/subscription/login')
          return
        }
      }

      message.error(
        error.response?.data?.message || '獲取訂閱資訊失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    if (!merchantOrderNo.trim()) {
      message.warning('請輸入訂單號')
      return
    }
    fetchSubscriptionData(merchantOrderNo.trim())
  }

  const handleLogout = () => {
    sessionStorage.removeItem('subscription_view_token')
    setToken(null)
    setSubscriptionData(null)
    setMerchantOrderNo('')
    navigate('/subscription/login')
  }

  const handleRefresh = () => {
    if (merchantOrderNo) {
      fetchSubscriptionData(merchantOrderNo)
    } else if (subscriptionData?.merchantOrderNo) {
      fetchSubscriptionData(subscriptionData.merchantOrderNo)
    }
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return '-'

    let date

    // 处理对象格式（MongoDB日期格式）
    if (typeof dateValue === 'object' && dateValue !== null) {
      if (dateValue.$date) {
        date = new Date(dateValue.$date)
      } else if (dateValue instanceof Date) {
        date = dateValue
      } else {
        return '-'
      }
    }
    // 处理字符串格式
    else if (typeof dateValue === 'string') {
      // 处理 YYYYMMDD 格式（如 "20260105"）
      if (dateValue.length === 8 && /^\d+$/.test(dateValue)) {
        return `${dateValue.substring(0, 4)}-${dateValue.substring(
          4,
          6
        )}-${dateValue.substring(6, 8)}`
      }
      // 处理 ISO 格式或其他标准日期格式
      date = new Date(dateValue)
    } else {
      return '-'
    }

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return dateValue.toString()
    }

    // 格式化日期（toLocaleString('zh-TW') 只設語系、不設時區）
    return formatTz(date, 'YYYY/MM/DD HH:mm')
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-'
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  // 计算财务数据（支持按月/按年两种方式）
  const calculateFinance = (subscriptionData, mode = 'monthly') => {
    if (!subscriptionData) return null

    const totalAmount = subscriptionData.amount || 0
    const paymentType = subscriptionData.paymentType || 'monthly'
    const periodTimes = subscriptionData.periodConfig?.periodTimes || 1

    let subscriptionTotalAmount
    let calculatedPeriodTimes

    if (mode === 'yearly') {
      // 按年计算：月订阅按12个月，年订阅只计算1年
      if (paymentType === 'monthly') {
        calculatedPeriodTimes = 12
        subscriptionTotalAmount = totalAmount * 12
      } else {
        // 年订阅只计算1年，不计算5年
        calculatedPeriodTimes = 1
        subscriptionTotalAmount = totalAmount * 1
      }
    } else {
      // 按月计算：按实际期数计算
      calculatedPeriodTimes = periodTimes
      subscriptionTotalAmount = totalAmount * periodTimes
    }

    // 蓝新手续费 = 订阅总金额 × 3%
    const newebpayFee = subscriptionTotalAmount * 0.03

    // 千金分润 = 订阅总金额 × 30%
    const qianjinCommission = subscriptionTotalAmount * 0.3

    // 实际收入 = 订阅总金额 - 蓝新手续费 - 千金分润
    const actualRevenue =
      subscriptionTotalAmount - newebpayFee - qianjinCommission

    return {
      subscriptionTotalAmount,
      newebpayFee,
      qianjinCommission,
      actualRevenue,
      periodTimes: calculatedPeriodTimes,
      singlePeriodAmount: totalAmount
    }
  }

  const getStatusTag = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: '啟用中' },
      inactive: { variant: 'default', text: '未啟用' },
      paused: { variant: 'error', text: '已暫停' },
      expired: { variant: 'warning', text: '已過期' }
    }
    const config = statusConfig[status] || { variant: 'default', text: status }
    return (
      <StatusTag variant={config.variant}>
        {status === 'active' && <CheckCircleOutlined />}
        {status !== 'active' && <CloseCircleOutlined />} {config.text}
      </StatusTag>
    )
  }

  const getAuthStatusTag = (status) => {
    const statusConfig = {
      success: { variant: 'success', text: '成功' },
      failed: { variant: 'error', text: '失敗' },
      pending: { variant: 'warning', text: '處理中' }
    }
    const config = statusConfig[status] || { variant: 'default', text: status }
    return <StatusTag variant={config.variant}>{config.text}</StatusTag>
  }

  const paymentRecordsColumns = [
    {
      title: '期數',
      dataIndex: 'periodNumber',
      key: 'periodNumber',
      width: 80,
      align: 'center'
    },
    {
      title: '付款日期',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (text) => formatDate(text)
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (text) => formatCurrency(text)
    },
    {
      title: '授權狀態',
      dataIndex: 'authStatus',
      key: 'authStatus',
      width: 100,
      align: 'center',
      render: (status) => getAuthStatusTag(status)
    },
    {
      title: '授權碼',
      dataIndex: 'authCode',
      key: 'authCode',
      width: 100,
      align: 'center'
    },
    {
      title: '授權時間',
      dataIndex: 'authTime',
      key: 'authTime',
      width: 160,
      render: (text) => formatDate(text)
    },
    {
      title: '交易序號',
      dataIndex: 'tradeNo',
      key: 'tradeNo',
      width: 150,
      ellipsis: true
    },
    {
      title: '已通知',
      dataIndex: 'notified',
      key: 'notified',
      width: 80,
      align: 'center',
      render: (notified) => (
        <CustomTag variant={notified ? 'success' : 'default'}>
          {notified ? '是' : '否'}
        </CustomTag>
      )
    }
  ]

  if (loading && !subscriptionData) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingContainer>
            <Spin size='large' />
          </LoadingContainer>
        </ContentWrapper>
      </PageContainer>
    )
  }

  const subscriptionMerchantOrderNo = subscriptionData?.merchantOrderNo

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>
            <CalendarOutlined /> 訂閱資訊查詢
          </Title>
          <ActionButtons>
            <StyledButton
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/subscription/list')}
            >
              返回列表
            </StyledButton>
            <StyledButton
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
            >
              登出
            </StyledButton>
          </ActionButtons>
        </Header>

        {/* 搜索框 */}
        <SearchContainer>
          <SearchInputWrapper>
            <label
              style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}
            >
              訂單號 (Merchant Order No)
            </label>
            <Input
              size='large'
              placeholder='請輸入訂單號，例如：PERIOD1767609312592610'
              value={merchantOrderNo}
              onChange={(e) => setMerchantOrderNo(e.target.value)}
              onPressEnter={handleSearch}
              disabled={loading}
              allowClear
              style={{
                borderRadius: '12px',
                borderColor: '#D4C4B0'
              }}
            />
          </SearchInputWrapper>
          <Button
            type='primary'
            size='large'
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
            style={{
              minWidth: '120px',
              background: 'linear-gradient(135deg, #A8B5C0 0%, #9CA3AF 100%)',
              border: 'none',
              borderRadius: '12px',
              fontWeight: 500,
              letterSpacing: '0.5px'
            }}
          >
            查詢
          </Button>
        </SearchContainer>

        {loading && subscriptionData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        )}

        {!subscriptionData && !loading && (
          <EmptyState>
            <p style={{ fontSize: '16px', marginBottom: '20px' }}>
              請輸入訂單號以查詢訂閱資訊
            </p>
          </EmptyState>
        )}

        {subscriptionData && !loading && (
          <>
            <Header style={{ marginTop: '20px', marginBottom: '0' }}>
              <Title>
                <CalendarOutlined /> 訂閱資訊詳情
              </Title>
              <ActionButtons>
                <StyledButton icon={<ReloadOutlined />} onClick={handleRefresh}>
                  重新整理
                </StyledButton>
              </ActionButtons>
            </Header>

            {(() => {
              const {
                merchantOrderNo: dataMerchantOrderNo,
                orderNo,
                paymentType,
                amount,
                customer,
                status,
                periodConfig,
                itemDesc,
                newebpayInfo,
                note,
                paymentRecords,
                createdAt,
                updatedAt,
                newebpayStatus,
                finance
              } = subscriptionData

              // 优先使用后端返回的财务数据，如果没有则使用前端计算
              const financeData =
                finance?.[calculationMode] ||
                calculateFinance(subscriptionData, calculationMode)

              return (
                <>
                  {/* 財務計算 */}
                  {financeData && (
                    <FinanceCard
                      title={
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            flexWrap: 'wrap',
                            gap: '12px'
                          }}
                        >
                          <span>
                            <DollarOutlined /> 財務計算
                          </span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              size='small'
                              type={
                                calculationMode === 'monthly'
                                  ? 'primary'
                                  : 'default'
                              }
                              onClick={() => setCalculationMode('monthly')}
                              style={{
                                borderRadius: '8px',
                                background:
                                  calculationMode === 'monthly'
                                    ? 'linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%)'
                                    : '#f5f5f0',
                                border:
                                  calculationMode === 'monthly'
                                    ? 'none'
                                    : '1px solid #d4c4b0',
                                color:
                                  calculationMode === 'monthly'
                                    ? '#ffffff'
                                    : '#5a5a5a'
                              }}
                            >
                              按月計算
                            </Button>
                            <Button
                              size='small'
                              type={
                                calculationMode === 'yearly'
                                  ? 'primary'
                                  : 'default'
                              }
                              onClick={() => setCalculationMode('yearly')}
                              style={{
                                borderRadius: '8px',
                                background:
                                  calculationMode === 'yearly'
                                    ? 'linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%)'
                                    : '#f5f5f0',
                                border:
                                  calculationMode === 'yearly'
                                    ? 'none'
                                    : '1px solid #d4c4b0',
                                color:
                                  calculationMode === 'yearly'
                                    ? '#ffffff'
                                    : '#5a5a5a'
                              }}
                            >
                              按年計算
                            </Button>
                          </div>
                        </div>
                      }
                    >
                      <FinanceGrid>
                        <FinanceItem>
                          <FinanceLabel>單期金額</FinanceLabel>
                          <FinanceValue>
                            {formatCurrency(financeData.singlePeriodAmount)}
                          </FinanceValue>
                        </FinanceItem>
                        <FinanceItem>
                          <FinanceLabel>總期數</FinanceLabel>
                          <FinanceValue>
                            {financeData.periodTimes} 期
                          </FinanceValue>
                        </FinanceItem>
                        <FinanceItem>
                          <FinanceLabel>訂閱總金額</FinanceLabel>
                          <FinanceValueLarge>
                            {formatCurrency(
                              financeData.subscriptionTotalAmount
                            )}
                          </FinanceValueLarge>
                        </FinanceItem>
                        <FinanceItem>
                          <FinanceLabel>藍新手續費 (3%)</FinanceLabel>
                          <FinanceValue style={{ color: '#9CA3AF' }}>
                            {formatCurrency(financeData.newebpayFee)}
                          </FinanceValue>
                        </FinanceItem>
                        <FinanceItem>
                          <FinanceLabel>千金分潤 (30%)</FinanceLabel>
                          <FinanceValue style={{ color: '#A8B5C0' }}>
                            {formatCurrency(financeData.qianjinCommission)}
                          </FinanceValue>
                        </FinanceItem>
                        <FinanceItem>
                          <FinanceLabel>實際收入</FinanceLabel>
                          <FinanceValueLarge>
                            {formatCurrency(financeData.actualRevenue)}
                          </FinanceValueLarge>
                        </FinanceItem>
                      </FinanceGrid>
                    </FinanceCard>
                  )}

                  {/* 訂單基本資訊 */}
                  <InfoCard
                    title={
                      <span>
                        <CreditCardOutlined /> 訂單基本資訊
                      </span>
                    }
                  >
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>訂單號</InfoLabel>
                        <InfoValue>{dataMerchantOrderNo || '-'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>系統訂單號</InfoLabel>
                        <InfoValue>{orderNo || '-'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>訂閱狀態</InfoLabel>
                        <InfoValue>{getStatusTag(status)}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>付款方式</InfoLabel>
                        <InfoValue>
                          {paymentType === 'monthly'
                            ? '月繳'
                            : paymentType === 'yearly'
                            ? '年繳'
                            : paymentType || '-'}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>方案名稱</InfoLabel>
                        <InfoValue>{itemDesc || '-'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>金額</InfoLabel>
                        <InfoValue
                          style={{ color: '#667eea', fontSize: '18px' }}
                        >
                          {formatCurrency(amount)}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>藍新金流狀態</InfoLabel>
                        <InfoValue>
                          <CustomTag
                            variant={
                              newebpayStatus === 'SUCCESS' ? 'success' : 'error'
                            }
                          >
                            {newebpayStatus || '-'}
                          </CustomTag>
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>備註</InfoLabel>
                        <InfoValue>{note || '-'}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </InfoCard>

                  {/* 客戶資訊 */}
                  <InfoCard
                    title={
                      <span>
                        <UserOutlined /> 客戶資訊
                      </span>
                    }
                  >
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>姓名</InfoLabel>
                        <InfoValue>{customer?.name || '-'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>電子郵件</InfoLabel>
                        <InfoValue>{customer?.email || '-'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>電話</InfoLabel>
                        <InfoValue>{customer?.phone || '-'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>用戶ID</InfoLabel>
                        <InfoValue>{customer?.userId || '未綁定'}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </InfoCard>

                  {/* 付款資訊 */}
                  {newebpayInfo && (
                    <InfoCard
                      title={
                        <span>
                          <DollarOutlined /> 付款資訊
                        </span>
                      }
                    >
                      <InfoGrid>
                        <InfoItem>
                          <InfoLabel>授權銀行</InfoLabel>
                          <InfoValue>{newebpayInfo.authBank || '-'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>授權碼</InfoLabel>
                          <InfoValue>{newebpayInfo.authCode || '-'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>卡片後四碼</InfoLabel>
                          <InfoValue>
                            {newebpayInfo.cardLastFour || '-'}
                          </InfoValue>
                        </InfoItem>
                      </InfoGrid>
                    </InfoCard>
                  )}

                  {/* 週期配置 */}
                  {periodConfig && (
                    <InfoCard
                      title={
                        <span>
                          <CalendarOutlined /> 週期配置
                        </span>
                      }
                    >
                      <InfoGrid>
                        <InfoItem>
                          <InfoLabel>週期類型</InfoLabel>
                          <InfoValue>
                            {periodConfig.periodType === 'M'
                              ? '月'
                              : periodConfig.periodType === 'Y'
                              ? '年'
                              : periodConfig.periodType === 'D'
                              ? '日'
                              : periodConfig.periodType || '-'}
                          </InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>扣款日</InfoLabel>
                          <InfoValue>
                            {periodConfig.periodPoint
                              ? `${periodConfig.periodPoint} 號`
                              : '-'}
                          </InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>首次付款日期</InfoLabel>
                          <InfoValue>
                            {formatDate(periodConfig.periodFirstDate)}
                          </InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>總期數</InfoLabel>
                          <InfoValue>
                            {periodConfig.periodTimes || '-'}
                          </InfoValue>
                        </InfoItem>
                      </InfoGrid>
                      {periodConfig.dateArray &&
                        periodConfig.dateArray.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <InfoLabel
                              style={{ display: 'block', marginBottom: '8px' }}
                            >
                              付款日期列表
                            </InfoLabel>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px'
                              }}
                            >
                              {periodConfig.dateArray.map((date, index) => (
                                <CustomTag key={index} variant='blue'>
                                  {formatDate(date)}
                                </CustomTag>
                              ))}
                            </div>
                          </div>
                        )}
                    </InfoCard>
                  )}

                  {/* 付款記錄 */}
                  {paymentRecords && paymentRecords.length > 0 && (
                    <InfoCard
                      title={
                        <span>
                          <DollarOutlined /> 付款記錄
                        </span>
                      }
                    >
                      <PaymentRecordsTable
                        columns={paymentRecordsColumns}
                        dataSource={paymentRecords}
                        rowKey='_id'
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showTotal: (total) => `共 ${total} 筆記錄`
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                    </InfoCard>
                  )}

                  {/* 時間資訊 */}
                  <InfoCard
                    title={
                      <span>
                        <CalendarOutlined /> 時間資訊
                      </span>
                    }
                  >
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>建立時間</InfoLabel>
                        <InfoValue>{formatDate(createdAt)}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>最後更新時間</InfoLabel>
                        <InfoValue>{formatDate(updatedAt)}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </InfoCard>
                </>
              )
            })()}
          </>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default SubscriptionInfoPage
